import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

const base = {
  width: 16,
  height: 16,
  viewBox: "0 0 16 16",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  focusable: false,
};

export const IconOverview = (p: IconProps) => (
  <svg {...base} {...p}><path d="M2 11.5l3.5-4 2.5 2.5L13.5 4" /><path d="M10.5 4h3v3" /></svg>
);

export const IconSubscribers = (p: IconProps) => (
  <svg {...base} {...p}><circle cx="6" cy="5.5" r="2.25" /><path d="M2.5 14c.5-2.5 2-3.75 3.5-3.75S9 11.5 9.5 14" /><circle cx="11.5" cy="6" r="1.75" /><path d="M10 14c.35-1.75 1.2-2.5 2-2.5s1.65.75 2 2.5" /></svg>
);

export const IconSegments = (p: IconProps) => (
  <svg {...base} {...p}><rect x="2" y="2" width="5" height="5" rx="1" /><rect x="9" y="2" width="5" height="5" rx="1" /><rect x="2" y="9" width="5" height="5" rx="1" /><rect x="9" y="9" width="5" height="5" rx="1" /></svg>
);

export const IconContent = (p: IconProps) => (
  <svg {...base} {...p}><rect x="2.5" y="2" width="11" height="12" rx="1.5" /><path d="M5 5.5h6M5 8h6M5 10.5h3.5" /></svg>
);

export const IconCampaigns = (p: IconProps) => (
  <svg {...base} {...p}><path d="M2 4.5l6 3.5L14 4.5v7L8 15l-6-3.5z" /><path d="M8 8v7" /></svg>
);

export const IconPipeline = (p: IconProps) => (
  <svg {...base} {...p}>
    <circle cx="3.5" cy="4" r="1.6" /><circle cx="12.5" cy="8" r="1.6" /><circle cx="3.5" cy="12" r="1.6" />
    <path d="M5.1 4.6 10.9 7.4" /><path d="M10.9 8.6 5.1 11.4" />
  </svg>
);

export const IconSettings = (p: IconProps) => (
  <svg {...base} {...p}><circle cx="8" cy="8" r="2" /><path d="M8 1.5v1.5M8 13v1.5M14.5 8H13M3 8H1.5M12.2 3.8l-1 1M4.8 11.2l-1 1M12.2 12.2l-1-1M4.8 4.8l-1-1" /></svg>
);

export const IconPanel = (p: IconProps) => (
  <svg {...base} {...p}><rect x="2" y="3" width="12" height="10" rx="2" /><path d="M6.25 3v10" /></svg>
);

export const IconMoon = (p: IconProps) => (
  <svg {...base} {...p}><path d="M13.5 9.5A5.75 5.75 0 016.5 2.5a5.75 5.75 0 100 11 5.75 5.75 0 007-4z" /></svg>
);

export const IconSun = (p: IconProps) => (
  <svg {...base} {...p}><circle cx="8" cy="8" r="3" /><path d="M8 1v1.5M8 13.5V15M15 8h-1.5M2.5 8H1M12.9 3.1l-1 1M4.1 11.9l-1 1M12.9 12.9l-1-1M4.1 4.1l-1-1" /></svg>
);

export const IconBell = (p: IconProps) => (
  <svg {...base} {...p}><path d="M4 7.5a4 4 0 018 0c0 3.2 1.2 4.5 1.2 4.5H2.8S4 10.7 4 7.5z" /><path d="M6.6 13a1.5 1.5 0 002.8 0" /></svg>
);

export const IconTeam = (p: IconProps) => (
  <svg {...base} {...p}>
    <circle cx="5.5" cy="5" r="2" />
    <circle cx="10.5" cy="5.5" r="1.6" />
    <path d="M2.2 13c.4-2.2 1.7-3.3 3.3-3.3S8.4 10.8 8.8 13" />
    <path d="M9.2 13c.3-1.6 1.1-2.4 2-2.4s1.7.8 2 2.4" />
  </svg>
);

export const IconHealth = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M8 14s5.5-3.4 5.5-7.2A3.3 3.3 0 008 3.5 3.3 3.3 0 002.5 6.8C2.5 10.6 8 14 8 14z" />
  </svg>
);

export const IconAnalytics = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M2.5 13V8.5M6.5 13V3.5M10.5 13V6.5M13.5 13V9.5" />
  </svg>
);

export const IconRoadmap = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M3 13.5V2.5h4.2L8.5 4h4.5v9.5H3z" />
    <path d="M5.5 7.5h5M5.5 10h3.5" />
  </svg>
);

export const IconSearch = (p: IconProps) => (
  <svg {...base} {...p}><circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5L14 14" /></svg>
);

/** Halo + swash, from the Brand Hub illustration primitives (standard §7). */
export const HaloSwash = (p: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 96 96" aria-hidden="true" focusable={false} {...p}>
    <circle cx="48" cy="46" r="30" fill="none" stroke="#155EEF" strokeWidth="6" />
    <path d="M24 58 C 34.56 58, 40.32 44, 48 44 C 55.68 44, 61.44 58, 72 58"
      fill="none" stroke="#155EEF" strokeWidth="5" strokeLinecap="round" />
  </svg>
);

export const IconPlusCircle = (p: IconProps) => (
  <svg {...base} {...p}><circle cx="8" cy="8" r="6" /><path d="M8 5.5v5M5.5 8h5" /></svg>
);

export const IconSliders = (p: IconProps) => (
  <svg {...base} {...p}><path d="M3 4.5h10M3 11.5h10" /><circle cx="6" cy="4.5" r="1.6" /><circle cx="10" cy="11.5" r="1.6" /></svg>
);

export const IconChecklist = (p: IconProps) => (
  <svg {...base} {...p}><path d="M2.5 4.5l1.2 1.2 2.1-2.4M2.5 10.5l1.2 1.2 2.1-2.4" /><path d="M8 4.5h5.5M8 10.5h5.5" /></svg>
);

export const IconArrowUp = (p: IconProps) => (
  <svg {...base} {...p}><path d="M8 13V3M4 7l4-4 4 4" /></svg>
);

export const IconArrowDown = (p: IconProps) => (
  <svg {...base} {...p}><path d="M8 3v10M4 9l4 4 4-4" /></svg>
);

export const IconTrash = (p: IconProps) => (
  <svg {...base} {...p}><path d="M2.5 4h11M6.5 4V2.5h3V4M4 4l.7 9.5h6.6L12 4M6.75 6.75v4M9.25 6.75v4" /></svg>
);

/* ── Action icons. These replaced emoji and symbol glyphs (✎ 🖼 🔗 ⚙ ⌕ ✕ ⚠ 🔒
   ↶ ↷ 📄 ⚡ ＋ ✦ ⠿ ⋯), which the Altibbi tool standard forbids: they render
   in each platform's own colour emoji font, at their own size and weight. */

export const IconPencil = (p: IconProps) => (
  <svg {...base} {...p}><path d="M10.5 2.5l3 3L5.5 13.5H2.5v-3z" /><path d="M9 4l3 3" /></svg>
);

export const IconImage = (p: IconProps) => (
  <svg {...base} {...p}><rect x="2" y="3" width="12" height="10" rx="1.5" /><circle cx="5.75" cy="6.25" r="1.1" /><path d="M2.5 11.5l3.5-3 2.5 2 2-1.5 3 2.5" /></svg>
);

export const IconLink = (p: IconProps) => (
  <svg {...base} {...p}><path d="M6.75 9.25a2.5 2.5 0 003.54 0l2.12-2.12a2.5 2.5 0 00-3.54-3.54l-.7.7" /><path d="M9.25 6.75a2.5 2.5 0 00-3.54 0L3.59 8.87a2.5 2.5 0 003.54 3.54l.7-.7" /></svg>
);

export const IconParagraph = (p: IconProps) => (
  <svg {...base} {...p}><path d="M2.5 4h11M2.5 7h11M2.5 10h11M2.5 13h6" /></svg>
);

export const IconClose = (p: IconProps) => (
  <svg {...base} {...p}><path d="M4 4l8 8M12 4l-8 8" /></svg>
);

export const IconWarning = (p: IconProps) => (
  <svg {...base} {...p}><path d="M8 2.25L14.25 13.25H1.75z" /><path d="M8 6.5v3M8 11.4v.1" /></svg>
);

export const IconCheck = (p: IconProps) => (
  <svg {...base} {...p}><path d="M3 8.5l3 3 7-7" /></svg>
);

export const IconLock = (p: IconProps) => (
  <svg {...base} {...p}><rect x="3" y="7" width="10" height="7" rx="1.5" /><path d="M5.25 7V5a2.75 2.75 0 015.5 0v2" /></svg>
);

export const IconUndo = (p: IconProps) => (
  <svg {...base} {...p}><path d="M5.5 3.5L2.5 6.5l3 3" /><path d="M2.5 6.5h7a3.5 3.5 0 010 7H7" /></svg>
);

export const IconRedo = (p: IconProps) => (
  <svg {...base} {...p}><path d="M10.5 3.5l3 3-3 3" /><path d="M13.5 6.5h-7a3.5 3.5 0 000 7H9" /></svg>
);

export const IconDocument = (p: IconProps) => (
  <svg {...base} {...p}><path d="M4 1.75h5.5L12.5 4.75v9.5H4z" /><path d="M9.5 1.75v3h3M6 8h4.5M6 10.5h4.5" /></svg>
);

export const IconBolt = (p: IconProps) => (
  <svg {...base} {...p}><path d="M9 1.75L3.5 9h4L7 14.25 12.5 7h-4z" /></svg>
);

export const IconPlus = (p: IconProps) => (
  <svg {...base} {...p}><path d="M8 3v10M3 8h10" /></svg>
);

export const IconSparkle = (p: IconProps) => (
  <svg {...base} {...p}><path d="M8 2l1.4 4.6L14 8l-4.6 1.4L8 14l-1.4-4.6L2 8l4.6-1.4z" /></svg>
);

export const IconGrip = (p: IconProps) => (
  <svg {...base} {...p} stroke="none" fill="currentColor"><circle cx="6" cy="4" r="1.1" /><circle cx="10" cy="4" r="1.1" /><circle cx="6" cy="8" r="1.1" /><circle cx="10" cy="8" r="1.1" /><circle cx="6" cy="12" r="1.1" /><circle cx="10" cy="12" r="1.1" /></svg>
);

export const IconMore = (p: IconProps) => (
  <svg {...base} {...p} stroke="none" fill="currentColor"><circle cx="3.5" cy="8" r="1.25" /><circle cx="8" cy="8" r="1.25" /><circle cx="12.5" cy="8" r="1.25" /></svg>
);

export const IconPalette = (p: IconProps) => (
  <svg {...base} {...p}><path d="M8 2a6 6 0 100 12c.8 0 1.25-.6 1.25-1.25 0-.9-.75-1.15-.75-2 0-.7.55-1.25 1.25-1.25H11.5A2.5 2.5 0 0014 7a5.5 5 0 00-6-5z" /><circle cx="5" cy="7.25" r=".9" /><circle cx="7.75" cy="4.75" r=".9" /><circle cx="10.75" cy="5.75" r=".9" /></svg>
);
